<template>
  <div class="Evaluation-modal-overlay">
    <transition name="modal-expand">
      <div v-if="isVisible" class="Evaluation-modal" @click.stop>
        <div class="search-container">
          <font-awesome-icon
            class="window-close-icon"
            :icon="['fas', 'window-close']"
            @click="close"
          />
          <h3 style="font-size: 1.5rem; margin-bottom: 10px;">
            Assign Assessment Tasks
          </h3>
        </div>

        <div class="project-selector" style="margin-bottom: 16px;">
          <label for="project-select" style="font-weight: bold; margin-right: 8px;">
            Select {{ $labels.project }}:
          </label>

          <select
            id="project-select"
            v-model="selectedProjectId"
            @change="onProjectChange"
            style="padding: 6px 12px; border-radius: 4px; border: 1px solid #ccc; min-width: 220px;"
          >
            <option disabled value="">-- Select a Session --</option>
            <option
              v-for="project in projects"
              :key="project.project_id"
              :value="project.project_id"
            >
              {{ project.name }}
            </option>
          </select>
        </div>

        <template v-if="selectedProjectId">
          <div class="container-fluid">
            <vue-good-table
              :columns="columns"
              :rows="processedRows"
              :pagination-options="{ enabled: true, perPage: 10 }"
              :search-options="{ enabled: false }"
              :sort-options="{ enabled: true }"
            >
              <template #table-row="props">
                <template v-if="props.column.field === 'select'">
                  <div
                    class="center-icons"
                    @click="toggleCheckbox(props.row.assessment_task_id)"
                  >
                    <input
                      type="checkbox"
                      v-model="selectedAssessmentTasks"
                      :value="props.row.assessment_task_id"
                      :disabled="alreadyAssignedAssessmentTasks.includes(props.row.assessment_task_id)"
                      @click.stop
                    />
                  </div>
                </template>
                <span v-else-if="props.column.field === 'remove'" class="center-icons">
                  <font-awesome-icon
                    icon="trash-alt"
                    class="delete-icon"
                    title="Remove assessment task"
                    v-if="alreadyAssignedAssessmentTasks.includes(props.row.assessment_task_id)"
                    @click="removeAssessmentTask(props.row.assessment_task_id)"
                  />
                </span>

                <span v-else :title="String(props.formattedRow[props.column.field] ?? '')">
                  {{ props.formattedRow[props.column.field] }}
                </span>
              </template>

              <template #emptystate>
                <div style="text-align: center; padding: 20px;">
                  No Matching Tasks
                </div>
              </template>
            </vue-good-table>
          </div>
        </template>

        <div v-else style="text-align: center; padding: 40px; color: #888;">
          Please select a session above to view and assign tasks.
        </div>

        <div class="button-container">
          <button class="ins-close-button" @click="close()">Close</button>

          <button
            class="btn btn-primary rounded"
            @click="assignSelectedAssessmentTasks"
            :disabled="!selectedProjectId"
            style="width: 220px;"
          >
            Assign Assessment Tasks
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios';
import { VueGoodTable } from 'vue-good-table-next';
import 'vue-good-table-next/dist/vue-good-table-next.css';

export default {
  name: 'AssignAssessmentTaskModal',

  components: {
    VueGoodTable,
  },

  data() {
    return {
      projects: [],
      selectedProjectId: '',
      assessment_tasks: [],
      selectedAssessmentTasks: [],
      alreadyAssignedAssessmentTasks: [],
      perPage: 10,
      isVisible: false,
    };
  },
    computed: {
    columns() {
      return [
        {
          label: 'ATA',
          field: 'chapt_no',
          sortable: true,
          thClass: 'vgt-id-column',
          tdClass: 'vgt-id-column',
          filterOptions: {
            enabled: true,
            placeholder: 'All',
            filterDropdownItems: this.uniqueChapters,
            filterFn: (rowValue, filterValue) => {
              if (!filterValue) return true;
              return String(rowValue) === String(filterValue);
            }
          }
        },
        {
          label: 'Task No',
          field: 'task_no',
          sortable: true,
          thClass: 'vgt-id-column',
          tdClass: 'vgt-id-column',
          filterOptions: {
            enabled: true,
            placeholder: 'All',
            filterFn: (rowValue, filterValue) => {
              if (!filterValue) return true;
              return String(rowValue || '').toLowerCase().includes(String(filterValue).toLowerCase());
            }
          }
        },
        {
          label: 'Category',
          field: 'category',
          sortable: true,
          thClass: 'vgt-category-column',
          tdClass: 'vgt-category-column',
          filterOptions: {
            enabled: true,
            placeholder: 'All',
            filterDropdownItems: this.uniqueCategories,
            filterFn: (rowValue, filterValue) => {
              if (!filterValue) return true;
              return String(rowValue) === String(filterValue);
            }
          }
        },
        {
          label: 'Question',
          field: 'question',
          sortable: true,
          thClass: 'vgt-name-column',
          tdClass: 'vgt-name-column',
          filterOptions: {
            enabled: true,
            placeholder: 'All',
            filterFn: (rowValue, filterValue) => {
              if (!filterValue) return true;
              return String(rowValue || '').toLowerCase().includes(String(filterValue).toLowerCase());
            }
          }
        },
        {
          label: 'Assessment Type',
          field: 'assessment_type',
          sortable: true,
          thClass: 'vgt-category-column',
          tdClass: 'vgt-category-column',
          filterOptions: {
            enabled: true,
            placeholder: 'All',
            filterDropdownItems: this.uniqueTypes,
            filterFn: (rowValue, filterValue) => {
              if (!filterValue) return true;
              return String(rowValue) === String(filterValue);
            }
          }
        },
        { label: 'Select', field: 'select', sortable: false, thClass: 'vgt-select-column', tdClass: 'vgt-select-column' },
        { label: 'Remove', field: 'remove', sortable: false, thClass: 'vgt-select-column', tdClass: 'vgt-select-column' }
      ];
    },
    uniqueChapters() {
      return [...new Set(this.assessment_tasks.map(t => t.chapt_no).filter(Boolean))];
    },
    uniqueCategories() {
      return [...new Set(this.assessment_tasks.map(t => t.category).filter(Boolean))];
    },
    uniqueTypes() {
      return [...new Set(this.assessment_tasks.map(t => t.assessment_type).filter(Boolean))];
    },
    processedRows() {
      return this.assessment_tasks;
    },
  },
  methods: {
    close() {
      this.$emit('close');
    },

    async loadProjects() {
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };

        const response = await axios.get('/api/projects', { headers });
        this.projects = response.data;
      } catch (error) {
        console.error('Error fetching projects:', error);
      }
    },

    onProjectChange() {
      this.assessment_tasks = [];
      this.selectedAssessmentTasks = [];
      this.alreadyAssignedAssessmentTasks = [];

      if (this.selectedProjectId) {
        this.selectedProjectId = Number(this.selectedProjectId);
        this.loadData();
      }
    },

    toggleCheckbox(assessmentTaskId) {
      if (this.alreadyAssignedAssessmentTasks.includes(assessmentTaskId)) return;

      const index = this.selectedAssessmentTasks.indexOf(assessmentTaskId);

      if (index > -1) {
        this.selectedAssessmentTasks.splice(index, 1);
      } else {
        this.selectedAssessmentTasks.push(assessmentTaskId);
      }
    },

    async assignSelectedAssessmentTasks() {
      if (!this.selectedProjectId || this.selectedAssessmentTasks.length === 0) return;

      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        const project_id = this.selectedProjectId;

        const newIds = this.selectedAssessmentTasks.filter(
          id => !this.alreadyAssignedAssessmentTasks.includes(id)
        );

        if (newIds.length === 0) {
          window.alert('All selected Assessment Tasks are already in the Session.');
          this.selectedAssessmentTasks = [];
          return;
        }

        await axios.post(
          `/api/projects/${project_id}/add_assessment_tasks`,
          { assessment_task_ids: newIds },
          { headers }
        );

        window.alert('Assessment Tasks added successfully to the Session.');
        this.selectedAssessmentTasks = [];
        this.$emit('close');
      } catch (error) {
        console.error('Error assigning Assessment Tasks:', error);

        if (error.isOffline) {
          this.$emit('close');
          return;
        }

        window.alert('An error occurred while assigning Assessment Tasks. Please try again.');
      }
    },

    async removeAssessmentTask(assessment_task_id) {
      try {
        const confirmRemove = confirm(
          'Are you sure you want to remove this Assessment Task from the Session?'
        );

        if (!confirmRemove) return;

        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };

        const response = await axios.delete(
          `/api/assessment_tasks/${assessment_task_id}/projects/${this.selectedProjectId}`,
          { headers }
        );

        if (response.status === 200) {
          window.alert('Assessment Task successfully removed from Session.');
          this.loadData();
        }
      } catch (error) {
        console.error('Error removing Assessment Task:', error);

        if (error.isOffline) {
          this.$emit('close');
        }
      }
    },

    async loadData() {
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };

        const assignedResponse = await axios.get(
          `/api/projects/${this.selectedProjectId}/assessment_tasks`,
          { headers }
        );

        const assignedData = assignedResponse.data;

        const assignedTasks = Array.isArray(assignedData)
          ? assignedData
          : assignedData.assessment_tasks || assignedData.data || [];

        this.alreadyAssignedAssessmentTasks = assignedTasks.map(
          task => task.assessment_task_id
        );

        const allResponse = await axios.get('/api/assessment_tasks', { headers });

        this.assessment_tasks = Array.isArray(allResponse.data)
          ? allResponse.data
          : allResponse.data.data || [];

        this.selectedAssessmentTasks = [...this.alreadyAssignedAssessmentTasks];
      } catch (error) {
        console.error('Error fetching assessment task data:', error);
      }
    },
  },

  async mounted() {
    this.isVisible = true;
    await this.loadProjects();
  },
};
</script>

<style scoped>
.close-button {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
}

.modal-body {
  margin-bottom: 20px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
}

.confirm-button,
.cancel-button {
  padding: 10px 15px;
  margin-left: 10px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.confirm-button {
  background-color: #28a745;
  color: white;
}

.cancel-button {
  background-color: #dc3545;
  color: white;
}

.trainee-table {
  width: 100%;
  border-collapse: collapse;
}

.trainee-table th,
.trainee-table td {
  padding: 10px;
  border: 1px solid #ddd;
}

.trainee-table th {
  background-color: #f4f4f4;
}

.Evaluation-modal-overlay {
  position: fixed;
  top: 0px;
  left: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  width: 100vw;
  height: 100vh;
  z-index: 1;
}

.Evaluation-modal {
  background-color: #ffffff;
  overflow-y: auto;
  /* Enables vertical scrolling if needed */
  margin-top: 15px;
  max-width: 90vw;
  max-height: 96vh;
  min-width: max(65vw, 1100px);
  padding: 10px;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);
  position: fixed;
  display: flex;
  flex-direction: column;
  overflow-wrap: break-word;
  padding: 20px;
  z-index: 1002;
}

.modal-title {
  text-align: center;
  margin-bottom: 20px;
}

.assessment-table {
  width: 90%;
  border-collapse: collapse;
  align-items: center;
  margin: 0 auto;
}

.assessment-table th,
.assessment-table td {
  border: 1px solid #ddd;
  padding: 3px;
  text-align: left;
  font-size: 14px;
}

.assessment-table thead th {
  background-color: #f2f2f2;
  font-size: 15px;
}

.signature-section {
  margin-top: 20px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.signature-item label {
  width: 40%;
  display: block;
  margin-bottom: 5px;
}

.signature-placeholder,
.date-placeholder {
  border: 1px solid #ddd;
  padding: 5px;
  min-height: 30px;
  /* Adjust as needed */
}

.button-container {
  display: flex;
  justify-content: space-between;
  /* Aligns items to the right */
  align-items: center;
}

.ins-save-button {
  background-color: #007bff;
  border-color: #007bff;
  color: white;
  border: none;
  cursor: pointer;
  border-radius: 5px;
  padding: 5px 10px;
  margin-left: 20px;
  width: 100%;
  min-width: 90px;
  max-width: 100px;
}

.ins-submit-button {
  background-color: brown;
  border: 1px solid rgb(92, 23, 23);
  color: white;
  cursor: pointer;
  border-radius: 5px;
  padding: 5px 20px;
  min-width: 100px;
  max-width: 120px;
  margin-right: 5%;
  height: 35px;
}

.ins-close-button {
  background-color: #6c757d;
  border: 1px solid #4d5358;
  color: white;
  padding: 5px 20px;
  border-radius: 5px;
  margin-left: 5%;
}

.ins-close-button:hover {
  background-color: #4d5358;
}

.ins-clear-button {
  background-color: #f0ac9c;
  color: white;
  padding: 5px 10px;
  border-radius: 5px;
  margin-right: 0;
  width: 100%;
  min-width: 140px;
  max-width: 150px;
  border: 1px solid #c7715e;
}

.ins-save-button:disabled,
.ins-submit-button:disabled,
.ins-clear-button:disabled {
  background-color: rgb(216, 216, 216);
  cursor: not-allowed;
  color: grey
}

.ins-save-button:disabled:hover,
.ins-submit-button:disabled:hover,
.ins-clear-button:disabled:hover {
  background-color: rgb(216, 216, 216);
  color: grey
}

.ins-submit-button:hover {
  background-color: rgb(129, 33, 33);
}

.assessment-result {
  margin-left: 25%;
}

.task-performance {
  width: 50%;
  border-collapse: collapse;
  text-align: left;
  background-color: #f2f2f2;
  margin-left: 20px;
}

.task-performance th {
  /* Adjust the color to match your header background */
  font-weight: bold;
}


.task-performance input[type="text"] {
  width: 100%;
  border: none;
  padding: 4px;
  text-align: center;
  background-color: #f2f2f2;
}

.assessment-container {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-top: 20px;
}


.signature-block {
  display: flex;
  flex-direction: column;
  margin-right: 70px;
}


.signature-table {
  width: 100%;
}

.signature-table td {
  padding: 4px;
  /* Adjust padding as needed */
  vertical-align: middle;
  /* Aligns content of <td> vertically in the middle */
}

.signature-table .signature {
  text-align: left;
  /* Aligns the text to the left */
}

.trainee-signature {
  text-align: left;
}

.signature-table input[type="text"],
.signature-table input[type="date"] {
  width: 100%;
  border: none;
  border-bottom: 1px solid black;
  border-radius: 0px;
  background-color: transparent;
  outline: none;
  height: 20px;
}

@media (max-width: 768px) {

  /* Adjust the max-width as needed for iPad or other tablet sizes */
  .Evaluation-modal {
    padding: 5px;
    font-size: 0.9em;
    /* Reduce font size */
  }

  /* Further adjustments for other elements */
}


#selectAllSatisfactory {
  margin-left: 5px;
  border: 1px solid rgb(206, 13, 13);
}

.signature-pad {
  border-bottom: 1px solid #000;
  border-radius: 5px;
}

.right {
  display: flex;
  justify-content: flex-end;
  margin-right: 5%;
}

/* Add this CSS to your stylesheet */
.signature-image-wrapper {
  position: relative;
  display: inline-block;
}

.signature-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: transparent;
  pointer-events: none;
  /* Prevents interaction with the overlay */
}

.disabled {
  opacity: 0.5;
  /* Optionally reduce opacity to indicate disabled state */
}

.assess-button {
  border: 1px solid #555c57;
  border-radius: 2px;
  background-color: #d6d6d6;
}

.assess-button:hover {
  background-color: rgb(180, 180, 180);
}

.assess-button:disabled:hover {
  cursor: not-allowed;
}

.pass-button {
  border: 1px solid #507458;
  border-radius: 2px;
  background-color: #b0e6bd;
  color: #075e1a;
}

.pass-button:hover {
  background-color: #82d896;
}

.fail-button {
  border: 1px solid #745050;
  border-radius: 2px;
  background-color: #e6b0b0;
  color: #7a0606;
}

.fail-button:hover {
  background-color: #d88282;
}

.in-progress-button {
  border: 1px solid #a06e36;
  border-radius: 2px;
  background-color: #e4d09c;
  color: #724400;
}

.in-progress-button:hover {
  background-color: #d8b382;
}

.missing-task {
  background-color: #fce2e4;
  /* Light red background for missing tasks */
}

span:hover {
  cursor: pointer;
  color: blue;
}

.pagination-container {
  display: flex;
  margin-left: 3%;
}

.container-fluid {
  padding-top: 5px;
  width: 100%;
  padding-right: 15px;
  padding-left: 15px;
  margin-right: auto;
  margin-left: auto;
}

.search-container {
  display: flex;
  justify-content: space-between;
  padding: 10px;
  font-size: 16px;
  width: 80%;
}

button.btn {
  padding: 10px 10px;
  font-size: 16px;
  width: 180px;
  height: 40px;
  margin-top: 0%;
  margin-bottom: 0px;
  border: 1px solid rgb(0, 45, 63);
  background: #2952c4;
  color: white;
  cursor: pointer;
  text-align: center;
  transition: transform 0.2s, box-shadow 0.2s;
  float: right;
  margin-right: 30px;
}

button.btn:hover {
  background-color: #0c1a61;
  transition: transform 0.2s, box-shadow 0.2s;
}

.heading h1 {
  padding-top: 30px;
  padding-bottom: 10px;
  text-align: center;
  padding-left: 3%;

}

.back-button {
  /* Adjust the right position as needed */
  width: 100px;
  border-radius: 5px;
  background-color: #c1e0bf;
  float: left;
  margin-left: 30px;
  border: 2px solid black;
  padding: 3px;
}

button:hover {
  background-color: #7c927a;
  transition: transform 0.2s, box-shadow 0.2s;
}

.back-button:hover {
  background-color: #abc7a9;
  cursor: pointer;
}

.pagination-info {
  font-size: 14px;
  color: #666;
  /* Adjust the color as needed */
  position: absolute;
  top: 100px;
  left: 50%;
  /* Center horizontally */
  transform: translateX(-50%);
  /* Center horizontally */
}

/* Data table styles */
.data-table-trainee {
  width: 99%;
  margin-left: 0.5%;
  margin-right: 0.5%;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  border-collapse: collapse;
  margin-top: 0;
  align-items: left;
  margin-bottom: 30px;
}

.data-table-trainee th {
  border: 1px solid #000000bf;
  padding: 10px 5px;
  text-align: left;
  color: rgb(107, 107, 107);
  background-color: rgba(219, 219, 219, 0.804);
  margin-top: 0;
  font-family: 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Open Sans', 'Helvetica Neue', sans-serif;
  font-size: 14px;
}

.data-table-trainee tbody tr:nth-child(odd) {
  background-color: white;
}

.data-table-trainee tbody tr:nth-child(even) {
  background-color: rgba(219, 219, 219, 0.804);
}


.data-table-trainee td {
  border: 1px solid #000000bf;
  padding: 10px 5px;
  text-align: left;
  font-weight: 500;
  margin-top: 0;
  font-family: 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Open Sans', 'Helvetica Neue', sans-serif;
  font-size: 14px;
}

.data-table-trainee,
.data-table-trainee th,
.data-table-trainee td {
  border: 1px solid black;
}

.data-table-trainee th,
.data-table-trainee td {
  padding: 8px;
}

.data-table-trainee td.center-icons {
  text-align: center;
}

.delete-icon {
  font-size: 16px;
  color: #FF0000;
  /* Red color */
  cursor: pointer;
  transition: color 0.2s, transform 0.2s, box-shadow 0.2s;
}

.delete-icon:hover {
  color: #a82222;
  /* Lighter red color on hover */
  cursor: pointer;
  transform: scale(1.1);
  /* Scale the icon on hover */
  /* You can also add a box-shadow or any other styles you prefer */
}

.modal-content {
  position: relative;
  max-height: 90vh;
  overflow-y: auto;
  padding: 15px;
}

.type-column {
  width: 12%;
}

.vgt-wrap__footer {
  margin-bottom: 0px !important;
}
</style>